import { useDrag, useDrop } from "react-dnd";
import { calculateDropPosition } from "../utils/allocation-drop-utils";

type BaseAllocationRow = {
  id: number;
  position: number;
  is_excluded: boolean;
};

type UseAllocationDragDropProps<T extends BaseAllocationRow> = {
  row: T;
  totalRowsInSection: number;
  dndType: string;
  onDrop: (
    draggedRow: T,
    dropTargetRow: T & { totalRowsInSection: number },
  ) => void;
};

type UseAllocationDragDropReturn = {
  drag: (node: HTMLElement | null) => void;
  drop: (node: HTMLElement | null) => void;
  isDragging: boolean;
  showDropIndicator: boolean;
  showIndicatorBelow: boolean;
};

/*
drag-drop hook for allocation rows
handles visual feedback (drop indicators, dragging opacity) and delegates row reordering to parent
uses calculated drop position to show indicator above/below target based on drag direction
*/
export const useAllocationDragDrop = <T extends BaseAllocationRow>({
  row,
  totalRowsInSection,
  dndType,
  onDrop,
}: UseAllocationDragDropProps<T>): UseAllocationDragDropReturn => {
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: dndType,
      item: () => row,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [row],
  );

  const [{ isOver, canDrop, draggedItem }, drop] = useDrop(
    () => ({
      accept: dndType,
      drop: (draggedItem: T) => {
        if (draggedItem.id !== row.id) {
          // pass the row with totalRowsInSection included
          onDrop(draggedItem, { ...row, totalRowsInSection });
        }
      },
      canDrop: (draggedItem: T) => {
        // allow dropping on any row except itself
        return draggedItem.id !== row.id;
      },
      collect: (monitor) => {
        const isOver = monitor.isOver({ shallow: true });
        const canDrop = monitor.canDrop();
        const draggedItem = monitor.getItem() as T | null;
        return { isOver, canDrop, draggedItem };
      },
    }),
    [row, onDrop, totalRowsInSection],
  );

  const showDropIndicator = isOver && canDrop && !isDragging;

  // determine indicator position using shared calculation logic
  let showIndicatorBelow = false;
  if (draggedItem) {
    const { shouldInsertBelow } = calculateDropPosition({
      draggedRow: draggedItem,
      dropTargetRow: { ...row, totalRowsInSection },
    });
    showIndicatorBelow = shouldInsertBelow;
  }

  return {
    drag,
    drop,
    isDragging,
    showDropIndicator,
    showIndicatorBelow,
  };
};
